import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, MessageCircle, MapPin, CheckCircle2 } from 'lucide-react';
const SERVICES_LIST = ['Fitness Coaching', 'Nutrition & Meal Prep', 'Rest & Recovery', 'Virtual Assistant', 'Executive Support', 'Small Business Compliance', 'Cleaning Services', 'Property Management', 'Relocation & Settling-In', 'Travel Planning', 'Event Planning', 'Personal Concierge', 'Digital Learning'];
const COURSES_LIST = ['Strength Foundations', 'HIIT & Burn', 'Meal Prep Mastery', 'Intuitive Eating Workshop', 'Small Business Compliance 101', 'Executive Presence', 'The Clarity Method', 'Digital Declutter'];
const TABS = [{
  id: 'general',
  label: 'General Contact'
}, {
  id: 'service',
  label: 'Service Application'
}, {
  id: 'course',
  label: 'Course Registration'
}, {
  id: 'newsletter',
  label: 'Newsletter'
}];
export function Contact() {
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    if (formData.get('bot-field')) return;
    const newErrors: Record<string, string> = {};
    const email = formData.get('email') as string;
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      newErrors.email = 'Valid email required';
    }
    if (formData.has('name') && !formData.get('name')) {
      newErrors.name = 'Required';
    }
    if (formData.has('firstName') && !formData.get('firstName')) {
      newErrors.firstName = 'Required';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setFormState('loading');
    setTimeout(() => setFormState('success'), 1500);
  };
  const resetForm = () => {
    setFormState('idle');
    setErrors({});
  };
  return <main className="flex flex-col min-h-screen bg-warmwhite pt-20" data-id="element-259" data-mp="1h8is49ovlmuq">
      {/* HERO SECTION */}
      <section className="bg-plum py-20 text-center px-4" data-id="element-260" data-mp="1w59kw041o0bd">
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="font-display text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-261" data-mp="1w4rpf5p7hq4i">
          Let's talk.
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-lg text-warmwhite/80 max-w-2xl mx-auto font-body" data-id="element-262" data-mp="yfofvnin08u7">
          Whether you need a service, want to register for a course, or just
          have a question — we're here.
        </motion.p>
      </section>

      <section className="py-24" data-id="element-263" data-mp="2rdv3uubsi939">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-264" data-mp="2rctqgj7axaid">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-265" data-mp="2w64wwj87bp9r">
            {/* FORMS AREA */}
            <div className="lg:col-span-2 bg-white rounded-3xl shadow-sm border border-charcoal/5 overflow-hidden" data-id="element-266" data-mp="2hhs3nu0i0kiy">
              {/* Tabs */}
              <div className="flex overflow-x-auto border-b border-charcoal/10 hide-scrollbar" data-id="element-267" data-mp="3a54mpu7gaa7i">
                {TABS.map(tab => <button key={tab.id} onClick={() => {
                setActiveTab(tab.id);
                resetForm();
              }} className={`relative px-6 py-5 text-sm font-medium whitespace-nowrap transition-colors ${activeTab === tab.id ? 'text-fuchsia' : 'text-charcoal/60 hover:text-charcoal'}`} data-id="element-268" data-mp="3f3666g8sgbji">
                    {tab.label}
                    {activeTab === tab.id && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-fuchsia" data-id="element-269" data-mp="29yanjyj6fsxh" />}
                  </button>)}
              </div>

              {/* Form Content */}
              <div className="p-8 md:p-10" data-id="element-270" data-mp="1va62inwnj5bj">
                <AnimatePresence mode="wait" data-id="element-271" data-mp="1v71svb0f3hdh">
                  {formState === 'success' ? <motion.div key="success" initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.95
                }} className="text-center py-12" data-id="element-272" data-mp="2suslwysj6r2p">
                      <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-6" data-id="element-273" data-mp="3f5kainm2oga2" />
                      <h3 className="font-display text-2xl font-bold text-charcoal mb-2" data-id="element-274" data-mp="3j6vrk4qvrt6n">
                        Message Received!
                      </h3>
                      <p className="text-charcoal/70 mb-8" data-id="element-275" data-mp="3o4fh40t13sdw">
                        Thank you for reaching out. Our team will get back to
                        you shortly.
                      </p>
                      <button onClick={resetForm} className="px-8 py-3 bg-warmwhite text-charcoal rounded-full font-medium hover:bg-charcoal/5 transition-colors" data-id="element-276" data-mp="1pvz7u4uhv2o">
                        Send another message
                      </button>
                    </motion.div> : <motion.form key={activeTab} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} onSubmit={handleSubmit} className="space-y-6" data-id="element-277" data-mp="2cj8nhi5avj4u">
                      <input type="text" name="bot-field" className="hidden" data-id="element-278" data-mp="3owdve99my8if" />

                      {activeTab === 'general' && <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-279" data-mp="2m1ko69ikzu5">
                            <div data-id="element-280" data-mp="gehhgpbto1mm">
                              <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-281" data-mp="gdg0y5c1emxi">
                                Name
                              </label>
                              <input type="text" name="name" className={`w-full px-4 py-3 rounded-xl border ${errors.name ? 'border-red-500' : 'border-charcoal/20'} focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-warmwhite/50`} data-id="element-282" data-mp="qdgns1947l6g" />
                            </div>
                            <div data-id="element-283" data-mp="v050u8a7b24f">
                              <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-284" data-mp="v3ap5lb09zz3">
                                Email
                              </label>
                              <input type="email" name="email" className={`w-full px-4 py-3 rounded-xl border ${errors.email ? 'border-red-500' : 'border-charcoal/20'} focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-warmwhite/50`} data-id="element-285" data-mp="m71s2dai3dg4" />
                            </div>
                          </div>
                          <div data-id="element-286" data-mp="7bc96o0m3gfm">
                            <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-287" data-mp="7aan7oi259j4">
                              Subject
                            </label>
                            <select name="subject" className="w-full px-4 py-3 rounded-xl border border-charcoal/20 focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-warmwhite/50 appearance-none" data-id="element-288" data-mp="is1ivgefx4o0">
                              <option data-id="element-289" data-mp="4rquck0ls260">General Inquiry</option>
                              <option data-id="element-290" data-mp="4u3ijewyf8ri">Partnership</option>
                              <option data-id="element-291" data-mp="92clgk5bx6">Media</option>
                              <option data-id="element-292" data-mp="3w4tt4coiwl2q">Feedback</option>
                              <option data-id="element-293" data-mp="3r8mtf1mq05t8">Other</option>
                            </select>
                          </div>
                          <div data-id="element-294" data-mp="3ma1st13k3k49">
                            <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-295" data-mp="3h9e5apejdles">
                              Message
                            </label>
                            <textarea name="message" rows={4} className="w-full px-4 py-3 rounded-xl border border-charcoal/20 focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-warmwhite/50 resize-none" data-id="element-296" data-mp="3d8lw1w5nzgii"></textarea>
                          </div>
                        </>}

                      {activeTab === 'service' && <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-297" data-mp="18zpm04onevhk">
                            <div data-id="element-298" data-mp="1e155hsb6gtxu">
                              <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-299" data-mp="1izygne2zs8uu">
                                Name
                              </label>
                              <input type="text" name="name" className={`w-full px-4 py-3 rounded-xl border ${errors.name ? 'border-red-500' : 'border-charcoal/20'} focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-warmwhite/50`} data-id="element-300" data-mp="1nrzv96nmubo1" />
                            </div>
                            <div data-id="element-301" data-mp="tyi55qbbpmym">
                              <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-302" data-mp="yv8megyv6olm">
                                Email
                              </label>
                              <input type="email" name="email" className={`w-full px-4 py-3 rounded-xl border ${errors.email ? 'border-red-500' : 'border-charcoal/20'} focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-warmwhite/50`} data-id="element-303" data-mp="13siey5hjvgdo" />
                            </div>
                            <div data-id="element-304" data-mp="3jm90cgi2itz3">
                              <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-305" data-mp="3jkxumb52bc5c">
                                Phone
                              </label>
                              <input type="tel" name="phone" className="w-full px-4 py-3 rounded-xl border border-charcoal/20 focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-warmwhite/50" data-id="element-306" data-mp="25qhgs3jmppp3" />
                            </div>
                            <div data-id="element-307" data-mp="1rskhgzdsj56q">
                              <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-308" data-mp="1mrn36xvwivai">
                                Preferred Start Date
                              </label>
                              <input type="date" name="date" className="w-full px-4 py-3 rounded-xl border border-charcoal/20 focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-warmwhite/50" data-id="element-309" data-mp="1dyjq4qp64kge" />
                            </div>
                          </div>
                          <div data-id="element-310" data-mp="pe6ld4zophdl">
                            <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-311" data-mp="pd5ab0io4uvv">
                              Service Interested In
                            </label>
                            <select name="service" className="w-full px-4 py-3 rounded-xl border border-charcoal/20 focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-warmwhite/50 appearance-none" data-id="element-312" data-mp="ge0dbymr99lt">
                              <option value="" data-id="element-313" data-mp="1d3r9s3elx6u9">Select a service</option>
                              {SERVICES_LIST.map(s => <option key={s} value={s} data-id="element-314" data-mp="zbuig1krj0yx">
                                  {s}
                                </option>)}
                            </select>
                          </div>
                          <div data-id="element-315" data-mp="1vtqr2447hhn4">
                            <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-316" data-mp="1vv0rgzyj71wx">
                              Additional Details
                            </label>
                            <textarea name="message" rows={3} className="w-full px-4 py-3 rounded-xl border border-charcoal/20 focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-warmwhite/50 resize-none" data-id="element-317" data-mp="3blunk2er1x97"></textarea>
                          </div>
                        </>}

                      {activeTab === 'course' && <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-318" data-mp="37jzlgvozvfud">
                            <div data-id="element-319" data-mp="32k3kl5blg4j1">
                              <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-320" data-mp="32jtoczzyh14w">
                                Name
                              </label>
                              <input type="text" name="name" className={`w-full px-4 py-3 rounded-xl border ${errors.name ? 'border-red-500' : 'border-charcoal/20'} focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-warmwhite/50`} data-id="element-321" data-mp="hv73x4vs9zwv" />
                            </div>
                            <div data-id="element-322" data-mp="31jik3n9y83nm">
                              <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-323" data-mp="31jqzbltjnl1z">
                                Email
                              </label>
                              <input type="email" name="email" className={`w-full px-4 py-3 rounded-xl border ${errors.email ? 'border-red-500' : 'border-charcoal/20'} focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-warmwhite/50`} data-id="element-324" data-mp="3bflnsxkg8aco" />
                            </div>
                          </div>
                          <div data-id="element-325" data-mp="3u4s64jhbyzxy">
                            <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-326" data-mp="8673oq29eenf">
                              Course Selection
                            </label>
                            <select name="course" className="w-full px-4 py-3 rounded-xl border border-charcoal/20 focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-warmwhite/50 appearance-none" data-id="element-327" data-mp="3ucvvenukajb8">
                              <option value="" data-id="element-328" data-mp="lxc4hvhd5czc">Select a course</option>
                              {COURSES_LIST.map(c => <option key={c} value={c} data-id="element-329" data-mp="h5al7pls2lzt">
                                  {c}
                                </option>)}
                            </select>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-330" data-mp="19orbi1k34igx">
                            <div data-id="element-331" data-mp="14udddre1fx8k">
                              <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-332" data-mp="2z3s8sbxtqu1e">
                                Level
                              </label>
                              <select name="level" className="w-full px-4 py-3 rounded-xl border border-charcoal/20 focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-warmwhite/50 appearance-none" data-id="element-333" data-mp="3423hukgap0ik">
                                <option data-id="element-334" data-mp="2gfdkvxiszw4t">Beginner</option>
                                <option data-id="element-335" data-mp="2ldf027d3zbb0">Intermediate</option>
                                <option data-id="element-336" data-mp="26f4k06dv9mj3">All Levels</option>
                              </select>
                            </div>
                            <div data-id="element-337" data-mp="2bdegckbbut6a">
                              <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-338" data-mp="2beg33m1ph43u">
                                Payment Preference
                              </label>
                              <select name="payment" className="w-full px-4 py-3 rounded-xl border border-charcoal/20 focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-warmwhite/50 appearance-none" data-id="element-339" data-mp="9ljkne5guz9p">
                                <option data-id="element-340" data-mp="23vzpxzuj8cio">Card</option>
                                <option data-id="element-341" data-mp="1z3gcr3zrbjee">EFT</option>
                                <option data-id="element-342" data-mp="1z2nbujnhbo46">PayPal</option>
                              </select>
                            </div>
                          </div>
                        </>}

                      {activeTab === 'newsletter' && <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-343" data-mp="2msri6ys1y1yv">
                            <div data-id="element-344" data-mp="2hvraykjgut66">
                              <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-345" data-mp="2hsn0j5fer172">
                                First Name
                              </label>
                              <input type="text" name="firstName" className={`w-full px-4 py-3 rounded-xl border ${errors.firstName ? 'border-red-500' : 'border-charcoal/20'} focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-warmwhite/50`} data-id="element-346" data-mp="ni9l2qvl9k7z" />
                            </div>
                            <div data-id="element-347" data-mp="ge039xwbud1r">
                              <label className="block text-sm font-medium text-charcoal mb-2" data-id="element-348" data-mp="3euaztwar02qr">
                                Email
                              </label>
                              <input type="email" name="email" className={`w-full px-4 py-3 rounded-xl border ${errors.email ? 'border-red-500' : 'border-charcoal/20'} focus:border-fuchsia focus:ring-1 focus:ring-fuchsia outline-none bg-warmwhite/50`} data-id="element-349" data-mp="3jup91ho35iqd" />
                            </div>
                          </div>
                          <div data-id="element-350" data-mp="2thu1kj5jpmnp">
                            <label className="block text-sm font-medium text-charcoal mb-4" data-id="element-351" data-mp="2ti2gshp55422">
                              Interests
                            </label>
                            <div className="flex flex-wrap gap-4" data-id="element-352" data-mp="2js0rxnoyrhm3">
                              {['Wellness', 'Business', 'Lifestyle', 'Events', 'All'].map(interest => <label key={interest} className="flex items-center gap-2 cursor-pointer" data-id="element-353" data-mp="20ty95qn0egwq">
                                  <input type="checkbox" name="interests" value={interest} className="w-4 h-4 text-fuchsia border-charcoal/20 rounded focus:ring-fuchsia" data-id="element-354" data-mp="3v8qzba54ifha" />
                                  <span className="text-sm text-charcoal/80" data-id="element-355" data-mp="gzi12kuc239x">
                                    {interest}
                                  </span>
                                </label>)}
                            </div>
                          </div>
                        </>}

                      <button type="submit" disabled={formState === 'loading'} className="w-full py-4 bg-fuchsia text-white rounded-xl font-medium hover:bg-fuchsia-light transition-colors flex justify-center items-center mt-4 disabled:opacity-70" data-id="element-356" data-mp="3udd3dsdh5s65">
                        {formState === 'loading' ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-357" data-mp="1intzath34s8x" /> : 'Submit'}
                      </button>
                    </motion.form>}
                </AnimatePresence>
              </div>
            </div>

            {/* CONTACT DETAILS & MAP */}
            <div className="space-y-8" data-id="element-358" data-mp="24wr08w7z0b8i">
              <div className="bg-charcoal text-white rounded-3xl p-8 shadow-sm" data-id="element-359" data-mp="24vpkieg0f0kg">
                <h3 className="font-display text-2xl font-bold mb-8" data-id="element-360" data-mp="187jkk4u18wci">
                  Get in touch
                </h3>
                <div className="space-y-6" data-id="element-361" data-mp="1c9whc3qsxjx5">
                  <a href="mailto:hello@silvermark.co.za" className="flex items-center gap-4 group" data-id="element-362" data-mp="1h6dd9k48t2ks">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-fuchsia transition-colors" data-id="element-363" data-mp="1m3n1wxjt5a7k">
                      <Mail className="w-5 h-5" data-id="element-364" data-mp="2xkdauofm8g4v" />
                    </div>
                    <span className="text-white/80 group-hover:text-white transition-colors" data-id="element-365" data-mp="27wr6mosmgpw5">
                      hello@silvermark.co.za
                    </span>
                  </a>
                  <a href="tel:+27112345678" className="flex items-center gap-4 group" data-id="element-366" data-mp="2hv6vsm839p30">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-fuchsia transition-colors" data-id="element-367" data-mp="2cxf7012qqj31">
                      <Phone className="w-5 h-5" data-id="element-368" data-mp="1k7gchq5s1tqz" />
                    </div>
                    <span className="text-white/80 group-hover:text-white transition-colors" data-id="element-369" data-mp="1k99hl10qs5wv">
                      +27 11 234 5678
                    </span>
                  </a>
                  <a href="https://wa.me/27823456789" target="_blank" rel="noreferrer" className="flex items-center gap-4 group" data-id="element-370" data-mp="1u4uiswxzl25k">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-fuchsia transition-colors" data-id="element-371" data-mp="11f4c38yop2jh">
                      <MessageCircle className="w-5 h-5" data-id="element-372" data-mp="3hvai2b5byaj1" />
                    </div>
                    <span className="text-white/80 group-hover:text-white transition-colors" data-id="element-373" data-mp="336mlc1jqzuri">
                      +27 82 345 6789
                    </span>
                  </a>
                  <div className="pt-6 border-t border-white/10 flex gap-4" data-id="element-374" data-mp="3vus78rmjfpsx">
                    <a href="#" className="text-white/50 hover:text-fuchsia transition-colors text-sm font-medium" data-id="element-375" data-mp="3vsp6pdnree9y">
                      @silvermark_investments
                    </a>
                    <span className="text-white/20" data-id="element-376" data-mp="3quxgqp6od28e">•</span>
                    <a href="#" className="text-white/50 hover:text-fuchsia transition-colors text-sm font-medium" data-id="element-377" data-mp="3qxjpgs5axns7">
                      LinkedIn
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-charcoal/5 border border-charcoal/10 border-dashed rounded-3xl h-64 flex flex-col items-center justify-center text-charcoal/50" data-id="element-378" data-mp="2vphl8domj3gg">
                <MapPin className="w-8 h-8 mb-2 opacity-50" data-id="element-379" data-mp="2lyemvjn2x2kv" />
                <span className="font-medium" data-id="element-380" data-mp="2gzaegv7zb8vl">Johannesburg, South Africa</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section className="bg-gold py-24 text-center px-4" data-id="element-381" data-mp="39nphskhr02r3">
        <div className="max-w-3xl mx-auto" data-id="element-382" data-mp="39o8xdujxef1y">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-charcoal mb-6" data-id="element-383" data-mp="34ntnjcyurpoq">
            Book a free 20-minute clarity call
          </h2>
          <p className="text-lg text-charcoal/80 mb-10 font-body" data-id="element-384" data-mp="30mhwd30bywef">
            Not sure where to start? Let's chat. No pressure, no commitment —
            just clarity.
          </p>
          <button className="px-10 py-4 bg-plum text-white rounded-full font-medium text-lg hover:bg-plum-light transition-colors shadow-lg hover:shadow-xl" data-id="element-385" data-mp="1p61aory6njj4">
            Schedule Your Call
          </button>
        </div>
      </section>
    </main>;
}